import { DatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { Stack, TextField } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  handleDateTypeStart,
  handleDateTypeEnd,
} from '../helpers/dateRangeDateTypes';

const getLabel = (disableUserInput, label, placeholder) => {
  if (disableUserInput && placeholder) return placeholder;
  return label;
};

const DateRange = ({
  sx = { width: '470px' },
  startingDate,
  endDate,
  errorHandler = () => {},
  setPeriod = () => {},
  dateInUTC = false,
  rangeLimit = null,
  startPlaceholder = null,
  endPlaceholder = null,
  disableUserInput = true,
  DateLimiterErrorHandler = () => {},
}) => {
  const { t } = useTranslation('global');

  const [endDateError, setEndDateError] = useState(false);

  const handleStartingDateChange = (dateType, dateValue) => {
    setPeriod(state => ({
      ...state,
      startingDate: dateValue,
      formattedDate: {
        ...state.formattedDate,
        startingDate: handleDateTypeStart(dateType, dateValue),
      },
    }));
  };

  const handleEndingDateChange = (dateType, dateValue) => {
    setEndDateError(!dateValue);
    DateLimiterErrorHandler(!dateValue);
    setPeriod(state => ({
      ...state,
      endDate: dateValue || null,
      formattedDate: {
        ...state.formattedDate,
        endDate: dateValue ? handleDateTypeEnd(dateType, dateValue) : '',
      },
    }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack direction="row" flexWrap="nowrap" pb={1} gap={1} sx={sx}>
        <DatePicker
          disableFuture
          label={getLabel(
            disableUserInput,
            t('dateRagePicker.startingDate'),
            startPlaceholder
          )}
          value={startingDate}
          onError={error => errorHandler()}
          onChange={newValue => {
            handleStartingDateChange(dateInUTC, newValue);
          }}
          defaultCalendarMonth={
            endDate ? DateTime.fromJSDate(new Date(endDate)).toMillis() : null
          }
          renderInput={({ inputProps, ...params }) => (
            <TextField
              {...params}
              size="small"
              fullWidth
              data-testid="dateRangeStartingDate"
              sx={{
                backgroundColor: theme => theme.palette.background.paper,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme => theme.palette.divider,
                },
              }}
              inputProps={{
                ...inputProps,
                ...(startPlaceholder && {
                  placeholder: (!disableUserInput && startPlaceholder) || '',
                }),
                readOnly: disableUserInput,
                'data-testid': 'dateRangeStartingDateInput',
              }}
            />
          )}
        />
        <DatePicker
          size="small"
          disableFuture
          label={getLabel(
            disableUserInput,
            t('dateRagePicker.endingDate'),
            endPlaceholder
          )}
          value={endDate}
          disableHighlightToday={true}
          onError={error => {
            handleEndingDateChange(dateInUTC, null);
          }}
          onChange={newValue => {
            handleEndingDateChange(dateInUTC, newValue);
          }}
          maxDate={
            startingDate && rangeLimit
              ? DateTime.fromJSDate(new Date(startingDate))
                  .plus(rangeLimit)
                  .toJSDate()
              : undefined
          }
          minDate={
            startingDate && rangeLimit
              ? DateTime.fromJSDate(new Date(startingDate)).toJSDate()
              : undefined
          }
          defaultCalendarMonth={
            startingDate
              ? DateTime.fromJSDate(new Date(startingDate)).toMillis()
              : null
          }
          renderInput={({ inputProps, ...params }) => (
            <TextField
              {...params}
              size="small"
              fullWidth
              data-testid="dateRangeEndingDate"
              error={rangeLimit && endDateError}
              sx={{
                backgroundColor: theme => theme.palette.background.paper,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme => theme.palette.divider,
                },
              }}
              inputProps={{
                ...inputProps,
                ...(endPlaceholder && {
                  placeholder: (!disableUserInput && endPlaceholder) || '',
                }),
                readOnly: disableUserInput,
                'data-testid': 'dateRangeEndingDateInput',
              }}
              helperText={
                rangeLimit &&
                endDateError && (
                  <Trans
                    t={t}
                    i18nKey="dateRagePicker.limitError"
                    values={rangeLimit}
                  />
                )
              }
            />
          )}
        />
      </Stack>
    </LocalizationProvider>
  );
};

export default DateRange;
